import { api } from "@/shared/lib/axios/axios.api"
import { API_PREFIX } from "@/shared/lib/axios/axios.constants"
import type {
  AssistantFolder,
  AssistantFolderFilter,
  AssistantUsageTotals,
} from "./assistant.types"
import type { AssistantStatistics, StatisticsFilters } from "./assistant.statistics"
import {
  AssistantConversation,
  AssistantConversationSummary,
  AssistantConversationTranscript,
  AssistantPromptTemplate,
  AssistantPromptTemplateSummary,
} from "./assistant.types"

const ASSISTANT_API_PREFIX = `${API_PREFIX}/assistant`

export const getAssistantStatistics = async (filters: StatisticsFilters) => {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined)
  )
  const response = await api.get<AssistantStatistics>(
    `${ASSISTANT_API_PREFIX}/statistics`,
    { params }
  )
  return response.data
}

export const listAssistantConversations = async ({
  pageSize,
  offset,
  folderId,
}: {
  pageSize?: number
  offset?: number
  folderId?: AssistantFolderFilter
}) => {
  const response = await api.get<AssistantConversationSummary[]>(
    `${ASSISTANT_API_PREFIX}/conversations`,
    // Absent filters are left off the wire entirely rather than sent as undefined.
    {
      params: {
        ...(pageSize && { pageSize }),
        ...(offset && { offset }),
        ...(folderId === null
          ? { ungrouped: true }
          : folderId !== undefined
            ? { folderId }
            : {}),
      },
    }
  )
  return response.data
}

export const getAssistantConversation = async ({
  conversationId,
}: {
  conversationId: string
}) => {
  const response = await api.get<AssistantConversationTranscript>(
    `${ASSISTANT_API_PREFIX}/conversations/${conversationId}`
  )
  return response.data
}

export const cancelAssistantGeneration = async (
  conversationId: string,
  runId: string
) => {
  const response = await api.post<{ cancelled: boolean }>(
    `${ASSISTANT_API_PREFIX}/conversations/${conversationId}/runs/${runId}/cancel`
  )
  return response.data
}

/** Whether the caller may use the tool. Answers for everyone, so it is safe to call unguarded. */
export const getAssistantAccess = async () => {
  const response = await api.get<{ allowed: boolean; reviewEffortSupported?: boolean }>(
    `${ASSISTANT_API_PREFIX}/access`
  )
  return response.data
}

export const listAssistantPromptTemplates = async () => {
  const response = await api.get<AssistantPromptTemplateSummary[]>(
    `${ASSISTANT_API_PREFIX}/prompt-templates`
  )
  return response.data
}

export const getAssistantPromptTemplate = async (templateId: string) => {
  const response = await api.get<AssistantPromptTemplate>(
    `${ASSISTANT_API_PREFIX}/prompt-templates/${templateId}`
  )
  return response.data
}

export const createAssistantPromptTemplate = async (body: {
  title: string
  content: string
}) => {
  const response = await api.post<AssistantPromptTemplate>(
    `${ASSISTANT_API_PREFIX}/prompt-templates`,
    body
  )
  return response.data
}

/**
 * A whole rubric, not a patch of one — both fields always travel, as the server expects.
 *
 * 409 means the rubric is one of the seeded ones, which nobody owns and nobody may rewrite.
 */
export const updateAssistantPromptTemplate = async ({
  templateId,
  ...body
}: {
  templateId: string
  title: string
  content: string
}) => {
  const response = await api.patch<AssistantPromptTemplate>(
    `${ASSISTANT_API_PREFIX}/prompt-templates/${templateId}`,
    body
  )
  return response.data
}

export const deleteAssistantPromptTemplate = async (templateId: string) => {
  await api.delete(`${ASSISTANT_API_PREFIX}/prompt-templates/${templateId}`)
}

export const createAssistantConversation = async ({
  title,
  preview,
  promptTemplateId,
  sourceLanguage,
  targetLanguage,
  folderId,
}: {
  folderId?: string
  title?: string
  preview?: string
  /** Resolved and frozen server-side at creation, so editing the rubric later does not change
   * what this conversation was told. */
  promptTemplateId?: string
  sourceLanguage?: string
  targetLanguage?: string
}) => {
  const response = await api.post<AssistantConversation>(
    `${ASSISTANT_API_PREFIX}/conversations`,
    {
      ...(folderId && { folderId }),
      ...(title && { title }),
      ...(preview && { preview }),
      ...(promptTemplateId && { promptTemplateId }),
      ...(sourceLanguage && { sourceLanguage }),
      ...(targetLanguage && { targetLanguage }),
    }
  )
  return response.data
}

export const renameAssistantConversation = async ({
  conversationId,
  title,
}: {
  conversationId: string
  title: string
}) => {
  const response = await api.patch<void>(
    `${ASSISTANT_API_PREFIX}/conversations/${conversationId}`,
    { title }
  )
  return response.data
}

export const deleteAssistantConversation = async ({
  conversationId,
}: {
  conversationId: string
}) => {
  const response = await api.delete<void>(
    `${ASSISTANT_API_PREFIX}/conversations/${conversationId}`
  )
  return response.data
}

/**
 * What the assistant has spent, split by chat and evaluation.
 *
 * Read from the server's ledger rather than summed from messages: a message row dies with its
 * conversation, so summing them would report only what nobody has deleted yet.
 *
 * The window is left to the server, which defaults to seven days. Naming a number here would give
 * the screen a second place to disagree with the copy beside it.
 */
export const getAssistantUsage = async () => {
  const response = await api.get<AssistantUsageTotals[]>(
    `${ASSISTANT_API_PREFIX}/usage`
  )
  return response.data
}

export const listAssistantFolders = async () => {
  const response = await api.get<AssistantFolder[]>(
    `${ASSISTANT_API_PREFIX}/folders`
  )
  return response.data
}

export const createAssistantFolder = async (name: string) => {
  const response = await api.post<AssistantFolder>(
    `${ASSISTANT_API_PREFIX}/folders`,
    { name }
  )
  return response.data
}

export const renameAssistantFolder = async ({
  folderId,
  name,
}: {
  folderId: string
  name: string
}) => {
  await api.patch(`${ASSISTANT_API_PREFIX}/folders/${folderId}`, { name })
}

export const deleteAssistantFolder = async (folderId: string) => {
  await api.delete(`${ASSISTANT_API_PREFIX}/folders/${folderId}`)
}

export const moveAssistantConversation = async ({
  conversationId,
  folderId,
}: {
  conversationId: string
  folderId: string | null
}) => {
  await api.patch(
    `${ASSISTANT_API_PREFIX}/conversations/${conversationId}/folder`,
    { folderId }
  )
}
